<script setup>
import { eventCategories, events } from '@js/utils/event';
import { logEvents } from '@js/utils/toolbox';
import TravelEssentialsCard from '../components/TravelEssentials/TravelEssentialsCard.vue';

const { handleCardObserved, batchTimeout } = useTravelEssentialsAnalytics();
function useTravelEssentialsAnalytics () {
	// Batched event tracking
	const observedEvents = ref([]);
	const batchTimeout = ref(null);

	const analyticsEventMap = {
		bookingcom: events.travelEssentialsDisplayBooking,
		rentalcar: events.travelEssentialsDisplayRentalCar,
		airporttransfer: events.travelEssentialsDisplayAirportTransfer,
		forex: events.travelEssentialsDisplayForex,
		arlandaexpress: events.travelEssentialsDisplayArlandaExpress,
		airalo: events.travelEssentialsDisplayAiralo,
		northmill: events.travelEssentialsDisplayNorthmill
	};

	function handleCardObserved (itemId) {
		observedEvents.value.push(itemId);

		// Clear existing timeout
		if (batchTimeout.value) {
			clearTimeout(batchTimeout.value);
		}

		// Batch events with a small delay to collect multiple observations
		batchTimeout.value = setTimeout(() => {
			const eventsToLog = Array.from(observedEvents.value)
				.map(itemId => ({
					eventCategory: eventCategories.observe,
					event: analyticsEventMap[itemId]
				}))
				.filter(event => event.event); // Filter out unmapped events

			if (eventsToLog.length > 0) {
				logEvents(eventsToLog);
			}

			// Clear the observed events
			observedEvents.value = [];

			// Clear the timeout reference
			batchTimeout.value = null;
		}, 500); // 200ms delay to batch events
	}

	return {
		handleCardObserved
	};
}

const offers = js_params.travelEssentials;

// Cleanup on unmount
onBeforeUnmount(() => {
	if (batchTimeout.value) {
		clearTimeout(batchTimeout.value);
	}
});
</script>

<template>
  <article
    v-for="item in offers"
    :key="item.id"
    class="kallebakom__item__wrapper"
  >
    <TravelEssentialsCard
      :key="item.id"
      :item="item"
      @observed="handleCardObserved"
    />
  </article>
  <template v-if="!offers.length">
    <div class="box">
      <p>{{ $l('travelEssentials.ui.empty') }}</p>
    </div>
  </template>
</template>


<style lang="scss">
.abHeader__nav {
  overflow: hidden;
}

.kallebakom__item__container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  @include breakpoint_down(md) {
    flex-direction: column;
  }
}

.travel-essentials {
  main {
    @include breakpoint_down(md) {
      background-color: $color-grey-100;
    }
  }

  .box {
    display: none;

    @include breakpoint_up(md) {
      padding: 2rem;
    }
  }

  .sectionIntro h1 {
    @include breakpoint_up(md) {
      color: $white;
    }
  }

  [data-kalleview=rentalcar] .kallebakom__display,
  [data-kalleview=forex] .kallebakom__alternatives,
  [data-kalleview=forex] .kallebakom__display span:nth-child(2),
  [data-kalleview=arlandaexpress] .kallebakom__multimobile-content .kallebakom__item-content {
    display: none;
  }

  [data-kalleview=arlandaexpress] .kallebakom__multimobile-content {
    min-height: auto;
  }

  .kallebakom__item__wrapper{
    flex: calc(33% - 1rem) 1 1;

    @include breakpoint_up(lg) {
      max-width: 38rem;
    }
  }

  [data-kalleview=forex] .kallebakom-modal--inputbox {
    gap: 2rem;

    .share-modal--input {
      color: $black;
      -webkit-text-fill-color: $black;
      -webkit-opacity: 1;
    }
  }
}

.kallebakom__item__wrapper {
    width: 100%;
    flex: calc(50% - 1rem) 1 1;
    border-radius: $borderRadius-sm;
    overflow: hidden;

    @include breakpoint_down(md) {
      flex: 100%;
      box-shadow: $lightBoxShadow;
    }

    .kallebakom__item  {
      height: 100%;
      display: flex;
      width: 100%;

        @include breakpoint_down(md) {
          margin: 0;
        }
    }

    .kallebakom__item-content {
      text-decoration: none;
    }

    .kallebakom__multimobile-content {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    [data-kalleview=arlandaexpress] {
      .kallebakom__multimobile-content .kallebakom__arlanda--logo {
        bottom: 7rem;
      }
      .kallebakom__multimobile-content .kallebakom__arlanda--description {
        bottom: 8rem;

        @include breakpoint_down(lg) {
          display: none;
        }
      }
    }

    [data-kalleview=bookingcom] {
      .kallebakom__alternatives {
        display: flex;
      }

      .link-item.abBtn.abBtn--blueWhite.abBtn--flex.kallebakom_cta {
        margin-top: 1rem;
      }

    }
}

[data-kalleview=vaccindirekt] {
  background: $vaccindirekt--yellow;

  .kallebakom__item-content {
    @include breakpoint_down(md) {
      overflow: visible;
      align-content: center;
      flex-direction: row-reverse;
    }
  }

	.kallebakom__header .kallebakom__multimobilemobile-logo {
    padding: 1.8rem;
    background-color: $vaccindirekt--yellow;
	}

  .kallebakom__item-content--image {
    margin-right: -7rem;
    margin-bottom: -2rem;
    margin-top: 1rem;
    transform: scale(1.2);

    @include breakpoint_down(md) {
      margin-bottom: -6rem;
      margin-top: 0;
    }

    img {
      height: auto;
      margin: 0;
    }
  }

  .kallebakom__title {
    background-color: $vaccindirekt--orange;
    color: $white;
  }

  .kallebakom__display {
    padding-left: 1rem;
    margin: 8rem 0;

    @include breakpoint_down(md) {
      align-self: center;
      margin: 4rem 0 1rem;
    }

    > span {
      font-size: 1.4rem;

      &::before {
        display: none;
      }
    }
  }

  .kallebakom__multimobile-content {
    overflow: visible;
  }

  .kallebakom__huge-title {
    font-size: 2rem;
    margin-left: 1rem;
    max-width: 70%;
    font-weight: 600;
    line-height: 2.6rem;

    @include breakpoint_down(md) {
      line-height: 2.5rem;
      margin-top: 2rem;
      margin-bottom: -3rem;
    }
  }

  .link-item.abBtn.abBtn--blueWhite.abBtn--flex.kallebakom_cta {
    position: absolute;
    bottom: 1rem;
    width: calc(100% - 2rem);
    background-color: $color-blue-800;
    font-weight: normal;
  }
}

</style>
